import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Pencil, Trash2, Check, X, AtSign, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/hooks/useRealtime";
import { toast } from "sonner";

const ROLE_TONES = {
  admin: "bg-red-500",
  directeur: "bg-violet-500",
  chef: "bg-blue-600",
  superviseur: "bg-indigo-500",
  agent: "bg-emerald-500",
  auditeur: "bg-slate-500",
};

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

function initials(name) {
  return (name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
}

function renderMentions(text, tone = "text-blue-800") {
  const parts = text.split(/(@[\w.\-]+)/g);
  return parts.map((p, i) => {
    // Stable pseudo-key derived from position + content — parts array is deterministic
    // for a given text so using index is safe here (no reordering possible).
    const stableKey = `${i}-${p.slice(0, 8)}`;
    return p.startsWith("@")
      ? <span key={stableKey} className={`${tone} font-semibold bg-blue-100/70 rounded px-1`}>{p}</span>
      : <React.Fragment key={stableKey}>{p}</React.Fragment>;
  });
}

export default function DiscussionThread({ entityType, entityId, title = "Discussion équipe" }) {
  const { user } = useAuth();
  const isWriter = user?.role !== "auditeur";
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [users, setUsers] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const textareaRef = useRef(null);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    try {
      const { data } = await api.get("/messages", { params: { entity_type: entityType, entity_id: entityId } });
      setMessages(data);
    } catch (err) {
      console.warn("[DiscussionThread] load messages failed", err?.response?.status || err?.message);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    load();
    if (!entityId) return;
    const i = setInterval(load, 30000); // safety net poll
    return () => clearInterval(i);
  }, [load, entityId]);

  useRealtime((type, data) => {
    if (type === "message" && data?.entity_type === entityType && data?.entity_id === entityId) {
      load();
    }
  });

  useEffect(() => {
    // scroll bottom on new message
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((err) => {
      console.warn("[DiscussionThread] load users failed", err?.response?.status || err?.message);
    });
  }, []);

  const onContentChange = (e) => {
    const val = e.target.value;
    setContent(val);
    const caret = e.target.selectionStart || val.length;
    const upto = val.slice(0, caret);
    const m = upto.match(/@([\w.\-]*)$/);
    if (m) {
      setMentionQuery(m[1]);
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const insertMention = (u) => {
    const el = textareaRef.current;
    if (!el) return;
    const caret = el.selectionStart || content.length;
    const upto = content.slice(0, caret);
    const rest = content.slice(caret);
    const replaced = upto.replace(/@([\w.\-]*)$/, `@${u.email.split("@")[0]} `);
    setContent(replaced + rest);
    setMentionOpen(false);
    el.focus();
  };

  const filteredMention = users.filter((u) => {
    if (!mentionQuery) return true;
    const q = mentionQuery.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  }).slice(0, 6);

  const send = async (e) => {
    e?.preventDefault();
    if (!content.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { entity_type: entityType, entity_id: entityId, content });
      setContent("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/messages/${editing}`, { content: editContent });
      setEditing(null);
      setEditContent("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const removeMsg = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await api.delete(`/messages/${id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid="discussion-thread">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50/70 border-b border-slate-200">
        <div className="flex items-center gap-2 text-slate-700">
          <MessageSquare className="w-4 h-4 text-blue-800" />
          <span className="text-sm font-semibold">{title}</span>
          <span className="text-xs text-slate-500">· {messages.length} message{messages.length > 1 ? "s" : ""}</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-400 hidden sm:block">Chat équipe</span>
      </div>

      <div ref={listRef} className="max-h-72 overflow-y-auto p-3 space-y-3 bg-white" data-testid="discussion-list">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">
            <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-40" /> Aucun message. Commencez la discussion !
          </div>
        ) : messages.map((m) => {
          const mine = m.user_id === user?.id;
          const isEditing = editing === m.id;
          return (
            <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : "flex-row"}`} data-testid={`msg-${m.id}`}>
              <div className={`shrink-0 w-8 h-8 rounded-full text-white text-xs font-semibold grid place-items-center ${ROLE_TONES[m.user_role] || "bg-slate-500"}`} title={m.user_name}>
                {initials(m.user_name)}
              </div>
              <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                <div className={`flex items-baseline gap-2 mb-0.5 text-xs text-slate-500 ${mine ? "flex-row-reverse" : ""}`}>
                  <span className="font-semibold text-slate-700">{m.user_name}</span>
                  <span className="font-mono">{timeAgo(m.created_at)}{m.edited_at && " · modifié"}</span>
                </div>
                {isEditing ? (
                  <div className="flex flex-col gap-2 w-full">
                    <Textarea rows={2} value={editContent} onChange={(e) => setEditContent(e.target.value)} className="text-sm" />
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)}><X className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" onClick={saveEdit} data-testid={`msg-save-${m.id}`}><Check className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ) : (
                  <div className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                    mine
                      ? "bg-blue-800 text-white rounded-tr-sm"
                      : "bg-slate-100 text-slate-800 rounded-tl-sm"
                  } ${m.deleted ? "italic opacity-60" : ""}`}>
                    {renderMentions(m.content, mine ? "text-blue-100" : "text-blue-800")}
                  </div>
                )}
                {mine && !isEditing && !m.deleted && (
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => { setEditing(m.id); setEditContent(m.content); }} className="text-slate-400 hover:text-blue-800" title="Modifier" data-testid={`msg-edit-${m.id}`}>
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button onClick={() => removeMsg(m.id)} className="text-slate-400 hover:text-red-500" title="Supprimer" data-testid={`msg-delete-${m.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isWriter && (
        <form onSubmit={send} className="border-t border-slate-200 p-3 relative" data-testid="discussion-form">
          <div className="flex items-end gap-2">
            <div className="flex-1 relative">
              <Textarea
                ref={textareaRef}
                rows={2}
                value={content}
                onChange={onContentChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !mentionOpen) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Message… (utilisez @ pour mentionner un collègue)"
                className="text-sm resize-none pr-9"
                data-testid="discussion-input"
              />
              <AtSign className="w-4 h-4 absolute right-2.5 top-2.5 text-slate-400" />
              {mentionOpen && filteredMention.length > 0 && (
                <div className="absolute left-0 bottom-full mb-1 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden" data-testid="discussion-mention-list">
                  {filteredMention.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => insertMention(u)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm flex items-center gap-2"
                    >
                      <div className={`w-6 h-6 rounded-full text-white text-[10px] font-semibold grid place-items-center ${ROLE_TONES[u.role] || "bg-slate-500"}`}>
                        {initials(u.name)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-800 truncate">{u.name}</div>
                        <div className="text-[11px] text-slate-500 font-mono truncate">{u.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button type="submit" disabled={sending || !content.trim()} className="bg-blue-900 hover:bg-blue-800 h-11 shrink-0" data-testid="discussion-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
